import type { Blacklist } from '@empathybroker/tsclient-play/dist/models/blacklist'
import type { Redirection } from '@empathybroker/tsclient-play/dist/models/redirection'
import type { Synonym } from '@empathybroker/tsclient-play/dist/models/synonym'
import type {
  NextQueries,
  Promotion,
  QueryRankingDTO,
  RelatedTags,
} from '@empathyco/playboard-core-domain'
import type { Component } from 'vue'
import type { Store } from 'vuex/types'
import type { AuditItemPayload, ToolsMessages } from '../models'
import type { ExplainDownload } from '../models/domain/explain'
import type { EqualizeDTO } from '../modules/equalize'
import type { FeatureConfigProvider } from '../providers/interfaces/feature-config.provider'
import type { AuditObjectTypeConfig, AuditObjectTypeConfigProvider } from './interfaces'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { interfaces } from 'inversify'
import ConfigurationAuditDetails from '../components/audit/details/configuration-audit-details.component.vue'
import InstanceManagementAuditDetails from '../components/audit/details/instance-management-audit-details.component.vue'
import {
  AuditItemDetails,
  CurationToolAuditItemDetails,
  EqualizeAuditItemDetails,
  PromotionAuditItemDetails,
  QueryRankingAuditItemDetails,
} from '../components/audit/item/details'
import { TYPES } from '../config/inversify.types'
import { AuditObjectTypeEnum, AuditTypeEnum, FeatureIdEnum } from '../enums'
import { MESSAGES_CONSTS } from '../store/messages/messages.consts'

import Context = interfaces.Context

export function getPlatformAuditObjectTypeConfigProvider({
  container,
}: Context): AuditObjectTypeConfigProvider {
  const store = container.get<Store<any>>(TYPES.Store)
  const messages: { tools: ToolsMessages } =
    store.getters[`${MESSAGES_CONSTS.moduleName}/${MESSAGES_CONSTS.getters.messages}`]

  const getFeatureConfigProvider = container.get<FeatureConfigProvider>(TYPES.FeatureConfigProvider)

  return (objectType: string): AuditObjectTypeConfig => {
    const featureId = getFeatureId(objectType)
    const domain = getDomain(objectType)
    const featureConfing = getFeatureConfigProvider(featureId as FeatureIdEnum, { domain })
    const toolMessages = getToolMessages(objectType, messages)

    return {
      featureId,
      color: featureConfing.color,
      featureName: featureConfing.name,
      detailsComponent: getDetailsComponent(objectType),
      fieldNames: getFields(toolMessages),
      icon: featureConfing.icon,
      getItemQueries: payload => getItemQueries(objectType, payload),
    }
  }
}

function getDetailsComponent(objectType: string): Component {
  switch (objectType) {
    case AuditObjectTypeEnum.EQUALIZE:
      return EqualizeAuditItemDetails
    case AuditObjectTypeEnum.SEARCH_CONFIGURATION:
      return QueryRankingAuditItemDetails
    case AuditObjectTypeEnum.BROWSE_CONFIGURATION:
      return ConfigurationAuditDetails
    case AuditObjectTypeEnum.RELATED_TAGS:
    case AuditObjectTypeEnum.NEXT_QUERIES:
      return CurationToolAuditItemDetails
    case AuditTypeEnum.CONFIG.toUpperCase():
    case AuditObjectTypeEnum.CLIENT_CONFIG:
    case AuditObjectTypeEnum.ENDPOINT_CONFIG:
    case AuditObjectTypeEnum.FEATURE_CONFIG:
    case AuditObjectTypeEnum.INDEX_CLIENT:
    case AuditObjectTypeEnum.INDEX_FEED_CONFIGURATION:
    case AuditObjectTypeEnum.INDEX_SCHEMA_CONFIGURATION:
      return InstanceManagementAuditDetails
    case AuditObjectTypeEnum.PROMOTION:
      return PromotionAuditItemDetails
    default:
      return AuditItemDetails
  }
}

function getToolMessages(
  objectType: string,
  messages: { tools: ToolsMessages },
): ToolsMessages[keyof ToolsMessages] {
  const toolMessages = {
    [AuditObjectTypeEnum.EQUALIZE]: messages.tools.equalize,
    [AuditObjectTypeEnum.BLACKLIST]: messages.tools.blacklist,
    [AuditObjectTypeEnum.PROMOTION]: messages.tools.promotions,
    [AuditObjectTypeEnum.REDIRECTION]: messages.tools.redirections,
    [AuditObjectTypeEnum.SYNONYM]: messages.tools.synonyms,
    [AuditObjectTypeEnum.BROWSE_CONFIGURATION]: messages.tools.configurations,
    [AuditObjectTypeEnum.SEARCH_CONFIGURATION]: messages.tools.configurations,
    [AuditObjectTypeEnum.RELATED_TAGS]: messages.tools.relatedTags,
    [AuditObjectTypeEnum.NEXT_QUERIES]: messages.tools.nextQueries,
    [AuditTypeEnum.CONFIG.toUpperCase()]: messages.tools.instances,
  }
  return toolMessages[objectType] ?? ({} as ToolsMessages[keyof ToolsMessages])
}

function getFields(messages: ToolsMessages[keyof ToolsMessages]) {
  let fields = {}

  if ('aside' in messages) {
    if ('form' in messages.aside) {
      fields = Object.assign(fields, messages.aside.form)

      if (
        messages.aside.form &&
        typeof messages.aside.form === 'object' &&
        'field' in messages.aside.form
      ) {
        fields = Object.assign(fields, messages.aside.form.field)
      }
    }

    fields = Object.assign(fields, messages.aside)
  }

  if ('fields' in messages) {
    fields = Object.assign(fields, messages.fields)
  }

  return fields
}

function getDomain(objectType: string): DomainEnum {
  switch (objectType) {
    case AuditObjectTypeEnum.BROWSE_CONFIGURATION:
      return DomainEnum.BROWSE
    default:
      return DomainEnum.SEARCH
  }
}

function getFeatureId(objectType: string): FeatureIdEnum | string {
  switch (objectType) {
    case AuditObjectTypeEnum.EQUALIZE:
      return FeatureIdEnum.EQUALIZE
    case AuditObjectTypeEnum.BLACKLIST:
      return FeatureIdEnum.BLACKLIST
    case AuditObjectTypeEnum.PROMOTION:
      return FeatureIdEnum.PROMOTION
    case AuditObjectTypeEnum.REDIRECTION:
      return FeatureIdEnum.REDIRECTION
    case AuditObjectTypeEnum.SYNONYM:
      return FeatureIdEnum.SYNONYM
    case AuditObjectTypeEnum.RELATED_TAGS:
      return FeatureIdEnum.RELATED_TAGS
    case AuditObjectTypeEnum.NEXT_QUERIES:
      return FeatureIdEnum.NEXT_QUERIES
    case AuditObjectTypeEnum.SEARCH_CONFIGURATION:
    case AuditObjectTypeEnum.BROWSE_CONFIGURATION:
      return FeatureIdEnum.CONFIGURATION
    case AuditObjectTypeEnum.EXPLAIN:
      return FeatureIdEnum.EXPLAIN
    case AuditTypeEnum.CONFIG:
    case AuditTypeEnum.CONFIG.toUpperCase():
      return FeatureIdEnum.INSTANCE_MANAGEMENT
    case AuditObjectTypeEnum.CLIENT_CONFIG:
      return FeatureIdEnum.CLIENT_CONFIG
    case AuditObjectTypeEnum.ENDPOINT_CONFIG:
      return FeatureIdEnum.ENDPOINT_CONFIG
    case AuditObjectTypeEnum.FEATURE_CONFIG:
      return FeatureIdEnum.FEATURE_CONFIG
    case AuditObjectTypeEnum.INDEX_CLIENT:
      return FeatureIdEnum.INDEX_CLIENT
    case AuditObjectTypeEnum.INDEX_FEED_CONFIGURATION:
      return FeatureIdEnum.INDEX_FEED_CONFIGURATION
    case AuditObjectTypeEnum.INDEX_SCHEMA_CONFIGURATION:
      return FeatureIdEnum.INDEX_SCHEMA_CONFIGURATION
    default:
      return objectType
  }
}

function getItemQueries(objectType: string, payload: AuditItemPayload<unknown>): string[] {
  const queries: Record<string, (state: any) => string[]> = {
    [AuditObjectTypeEnum.BLACKLIST]: (state: Blacklist) => state.terms,
    [AuditObjectTypeEnum.PROMOTION]: (state: Promotion) => state.criteria.queries,
    [AuditObjectTypeEnum.REDIRECTION]: (state: Redirection) => state.keywords,
    [AuditObjectTypeEnum.SYNONYM]: (state: Synonym) => state.synonyms,
    [AuditObjectTypeEnum.SEARCH_CONFIGURATION]: (state: QueryRankingDTO) => state.criteria.queries,
    [AuditObjectTypeEnum.RELATED_TAGS]: (state: RelatedTags) => state.criteria.queries,
    [AuditObjectTypeEnum.NEXT_QUERIES]: (state: NextQueries) => state.criteria.queries,
    [AuditObjectTypeEnum.EQUALIZE]: (state: EqualizeDTO) => (state.title ? [state.title] : []),
    [AuditObjectTypeEnum.EXPLAIN]: (state: ExplainDownload) => [state.query],
    default: () => [],
  }
  const state = payload.nextState ?? payload.previousState
  return (queries[objectType] || queries.default)(state)
}
